
import { useState, useCallback, useEffect } from 'react';
import { TransportationDetails } from '@/types/itinerary';
import { useLocalStorage } from './useLocalStorage';
import { getInitialTransportationState } from './utils/transportationUtils';
import { useAuth } from '@/contexts/auth';

/**
 * Hook to manage transportation form state
 */
export const useTransportationFormState = () => {
  const { user } = useAuth();
  const initialState = getInitialTransportationState();
  
  const [outboundDetails, setOutboundDetails, loadOutboundDetails] = useLocalStorage(
    'outboundTravel',
    initialState
  );
  
  const [returnDetails, setReturnDetails, loadReturnDetails] = useLocalStorage(
    'returnTravel',
    initialState
  );

  // Set user name when component mounts
  useEffect(() => {
    // Get username from local storage if available
    const userName = localStorage.getItem('userName') || user?.email?.split('@')[0] || 'Anonymous';
    
    setOutboundDetails(prev => ({
      ...prev,
      userName: userName
    }));
    
    setReturnDetails(prev => ({
      ...prev,
      userName: userName
    }));
  }, [user]); 

  // Reset form to initial state
  const resetForm = useCallback((formType: 'outbound' | 'return') => {
    const userName = localStorage.getItem('userName') || user?.email?.split('@')[0] || 'Anonymous';
    
    if (formType === 'outbound') {
      setOutboundDetails({
        ...initialState,
        userName
      });
    } else {
      setReturnDetails({
        ...initialState,
        userName
      });
    }
  }, [user, setOutboundDetails, setReturnDetails]);

  // Handle field changes for outbound journey
  const handleOutboundChange = useCallback((field: keyof TransportationDetails, value: any) => {
    setOutboundDetails(prev => ({
      ...prev,
      [field]: value
    }));
  }, [setOutboundDetails]);

  // Handle field changes for return journey
  const handleReturnChange = useCallback((field: keyof TransportationDetails, value: any) => {
    setReturnDetails(prev => ({
      ...prev,
      [field]: value
    }));
  }, [setReturnDetails]);

  return {
    outboundDetails,
    returnDetails,
    handleOutboundChange,
    handleReturnChange,
    resetForm,
    setOutboundDetails,
    setReturnDetails
  };
};
